import React, { useEffect, useState } from "react";
import {
  Download,
  Edit,
  MoreHorizontal,
  Phone,
  Mail,
  Calendar,
  Hash,
  Droplet,
  Mars,
  User,
  Trash,
  IdCard,
} from "lucide-react";
import InfoRow from "./InfoRow";
import StatsGrid from "./StatsGrid";
import api from "../../../api";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { apiRequest, apiSuccess } from "../../../redux/api/apiActions";
import errorChecker from "../../../utils/errorChecker";

import EditePatient from "./EditePatient";
import {
  clearConfirmationAlerts,
  hideAlert,
  showAlert,
} from "../../../redux/alert/alertActions";
import { useUser } from "../../../authentication/authentication";
import textTranslator from "../../../utils/useTextTranslator";
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import { isAdminRole } from "../../../utils/adminChecker";
import { FaUserDoctor } from "react-icons/fa6";
import { checkAccess } from "../../../utils/usePageAccess";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { generatePDF } from "../../../utils/generatePDF";
import LoadingButton from "../../../components/common/LoadingButton.jsx";
/**
 * Text Component Wrapper for Waiting Room Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */

const Text = ({ children, className, page = "PATIENTS" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

const avatarSelector = (patient) => {
  const { gender, age } = patient;
  if (!patient)
    return "https://images.pexels.com/photos/7515590/pexels-photo-7515590.jpeg";

  if (gender === "Male") {
    if (age < 8) return "/images/avatars/young-boy.png";
    else if (age < 18) return "/images/avatars/boy.png";
    else if (age < 30) return "/images/avatars/adult-man.png";
    else if (age < 50) return "/images/avatars/man.png";
    else return "/images/avatars/old-man.png";
  } else {
    if (age < 8) return "/images/avatars/young-girl.png";
    else if (age < 18) return "/images/avatars/girl.png";
    else if (age < 30) return "/images/avatars/adult-woman.png";
    else if (age < 50) return "/images/avatars/woman.png";
    else return "/images/avatars/old-woman.png";
  }
};
function Avatar({ patient }) {
  return (
    <img
      src={avatarSelector(patient || {})}
      alt="Avatar"
      className="w-[120px] h-[120px] !max-w-[120px] max-h-[120px] rounded-full bg-slate-200  border-[3px] border-white shadow-[0_2px_8px_rgba(0,0,0,0.12)] object-cover"
    />
  );
}

function PatientHeaderPlaceholder({ darkMode = false }) {
  return (
    <div className="flex flex-col md:flex-row items-start justify-between gap-6">
      {/* Left: Avatar + Name + Info */}
      <div className="flex gap-4 items-start">
        {/* Avatar Placeholder */}
        <div
          className={`w-[120px] h-[120px] !max-w-[120px] max-h-[120px] rounded-full ${
            darkMode ? "bg-gray-700" : "bg-gray-200"
          } animate-pulse`}
        />

        <div>
          {/* Name Placeholder */}
          <div
            className={`h-7 w-48 rounded ${
              darkMode ? "bg-gray-700" : "bg-gray-200"
            } animate-pulse`}
          />

          {/* Info Grid Placeholder */}
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`h-5 w-32 rounded ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                } animate-pulse`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right: Action buttons */}
      <div className="flex gap-2 shrink-0 self-end md:self-auto">
        {["Download", "Edit", "Delete"].map((_, i) => (
          <div
            key={i}
            className={`h-9 w-20 rounded-lg ${
              darkMode ? "bg-gray-700" : "bg-gray-200"
            } animate-pulse`}
          />
        ))}
      </div>
    </div>
  );
}

function PersonalInfoCard({ setIsPatientExists }) {
  const { id } = useParams();
  const user = useUser();
  const navigate = useNavigate();
  const { requests } = useSelector((state) => state.api);
  const updatePatientKey = `updatePatient_${id}`;
  const patientKey = `getPatient_${id}`;
  const deletePatientKey = `deletePatient_${id}`;
  const deleteAllPatientsKey = `deleteAllPatients`;
  const patientLoading = requests[patientKey]?.loading;
  const deletePatient = requests[deletePatientKey];
  const deleteAllPatients = requests[deleteAllPatientsKey];
  const updatePatient = requests[updatePatientKey];
  const darkMode = useSelector((state) => state.settings.darkMode);
  const [downloading, setDownloading] = useState(false);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const dispatch = useDispatch();
  const [showEditModal, setShowEditModal] = useState(false);
  const [dob, setDob] = useState("");
  const [patient, setPatient] = useState(null);
  const [clinic,setClinic] = useState(null);
  useEffect(() => {
    dispatch(apiRequest("GET", patientKey));
    api
      .get(`/patients/patient-id/${id}`)
      .then((res) => {
        setPatient(res.data);
     
        setIsPatientExists(true);
        dispatch(
          apiSuccess("GET", patientKey, "Fetched patients successfully"),
        );
      })
      .catch((error) => {
        setIsPatientExists(false);
      });
  }, [
    id,
    updatePatient?.success,
    deletePatient?.success,
    deleteAllPatients?.success,
    dispatch,
  ]);

  const showEditModalHandler = () => {
    setShowEditModal(!showEditModal);
  };

  const handleDelete = () => {
    dispatch(clearConfirmationAlerts());
    dispatch(
      showAlert({
        id: `delete-confirmation-${patient.id}`,
        type: "confirmation",
        title: textTranslator("Delete Patient", "PATIENTS"),
        message: `${textTranslator(
          "Are you sure you want to delete patient",
          "PATIENTS",
        )} ${patient.id}? ${textTranslator(
          "This action cannot be undone.",
          "PATIENTS",
        )}`,
        onCancel: cancelDelete,
        onConfirm: () => confirmDelete(patient.id),
      }),
    );
  };
  const confirmDelete = () => {
    dispatch(apiRequest("DELETE", deletePatientKey));
    api
      .delete(`patients/user-id/${user.id}`, {
        data: {
          selectedIds: [patient.id],
        },
      })
      .then((res) => {
        dispatch(
          apiSuccess(
            "DELETE",
            deletePatientKey,
            "Patient deleted successfully",
          ),
        );

        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Deleted", "PATIENTS"),
            message: `${textTranslator("Patient", "PATIENTS")} ${
              element.id
            } ${textTranslator("patientDeleted", "PATIENTS")}`,
          }),
        );
        navigate("/patients");
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error ||
          "Erreur lors de la récupération des données.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  };
  const cancelDelete = (id) => {
    dispatch(hideAlert(id));
  };

 const handleDownload = async () => {
  try {
    setDownloading(true);
    await generatePDF({
      api,
      textTranslator,
      defaultLanguage,
      dispatch,
      showAlert,

      pageKey: "PATIENTS",

      titleData: {
        subtitle: `${textTranslator("Patient Record", "PATIENTS")} — ${textTranslator("Confidential", "PATIENTS")} · 
        ${textTranslator("Code", "PATIENTS")}: ${
          patient.patientCode || patient.id
        }`,
        filename: `patient_${patient.id}`,
      },

      sections: [
        {
          title: "Personal Information",
          items: [
            { label: "Patient ID", value: patient.id },
            { label: "CIN", value: patient.cin || textTranslator("None", "PATIENTS") },
            { label: "Date of Birth", value: `${formatDate(patient.birthday)} (${patient.age || "None"} ${textTranslator("years old", "PATIENTS")})` },
            { label: "Gender", value: textTranslator(patient.gender || "None", "PATIENTS") },
            { label: "Civil Status", value: textTranslator(patient.civilStatus || "None", "PATIENTS") },
            { label: "Blood Type", value: patient.bloodType || textTranslator("None", "PATIENTS") },
          ],
        },
        {
          title: "Contact Information",
          items: [
            { label: "Phone", value: patient.phone || textTranslator("None", "PATIENTS") },
            { label: "Email", value: patient.email || textTranslator("None", "PATIENTS") },
            { label: "Address", value: patient.address || textTranslator("None", "PATIENTS") },
          ],
        },
        {
          title: "Medical Information",
          items: [
            { label: "Allergies", value: patient.allergies || textTranslator("None", "PATIENTS") },
            { label: "Chronic Conditions", value: patient.chronicConditions || textTranslator("None", "PATIENTS") },
            { label: "Current Medications", value: patient.currentMedications || textTranslator("None", "PATIENTS") },
            {
              label: "Family Medical History",
              value: patient.familyMedicalHistory || textTranslator("None", "PATIENTS"),
            },
            { label: "Insurance Provider", value: patient.medicalInsurance || textTranslator("None", "PATIENTS") },
          ],
        },

        ...(patient.emergencyContact
          ? [
              {
                title: "Emergency Contact",
                items: [
                  { label: "Name", value: patient.emergencyContact.name || textTranslator("None", "PATIENTS") },
                  {
                    label: "Relationship",
                    value: patient.emergencyContact.relation || textTranslator("None", "PATIENTS"),
                  },
                  { label: "Phone", value: patient.emergencyContact.phone || textTranslator("None", "PATIENTS") },
                ],
              },
            ]
          : []),

        ...(patient.notes
          ? [
              {
                title: "Notes",
                items: [
                  { label: "Notes", value: patient.notes || textTranslator("None", "PATIENTS") },
                ],
              },
            ]
          : []),
      ],
      qrData: {
        type: "patient",
        id: patient.id,
        name: `${patient.firstName} ${patient.lastName}`,
        cin: patient.cin,
        birthday: patient.birthday,
        phone: patient.phone,
        email: patient.email,
      },
    });
    setDownloading(false);
  } catch (error) {
    setDownloading(false);
    console.error(error);
  }
};

  return (
    <div className="bg-white rounded-2xl p-5 md:px-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-slate-100">
      <>
        {patientLoading ||
        !patient ||
        deletePatient?.loading ||
        deleteAllPatients?.loading ? (
          <PatientHeaderPlaceholder darkMode={darkMode} />
        ) : (
          <div className="flex flex-col md:flex-row items-start justify-between gap-6 relative">
            {showEditModal && (
              <EditePatient
                patient={patient}
                onClose={() => setShowEditModal(false)}
              />
            )}
            {/* Left: Avatar + Name + Info */}
            <div className={arClass("flex flex-col md:flex-row gap-6 items-center md:items-start p-4 bg-white rounded-xl shadow-sm border border-gray-100 mt-12 lg:mt-0 w-full")}>
              {/* Avatar Section */}
              <div className="flex-shrink-0">
                <Avatar patient={patient} />
              </div>

              {/* Details Section */}
              <div className={arClass("w-full")}>
                <h1 className={arClass("text-xl font-bold text-gray-900 tracking-wide ")}>
                  {patient.firstName} {patient.lastName}
                </h1>

                {/* Info Rows Container - Switches to 2 columns on larger screens */}
                <div className={arClass("mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                  {/* Column 1 */}
                  <div className="flex flex-col gap-2">
                    <InfoRow
                      icon={Hash}
                      label={patient.id || "N/A"}
                      color="#00558d"
                    />
                    <InfoRow
                      icon={Phone}
                      label={patient.phone || "N/A"}
                      color="#00558d"
                    />
                    <InfoRow
                      icon={Mail}
                      label={patient.email || "N/A"}
                      color="#00558d"
                    />
                  </div>

                  {/* Column 2 */}
                  <div className="flex flex-col gap-2">
                    <InfoRow
                      icon={IdCard}
                      label={patient.cin || "N/A"}
                      color="#00558d"
                    />
                    <InfoRow icon={Calendar} label={`${formatDate(patient.birthday)} (${patient.age || "None"} ${textTranslator("years old", "PATIENTS")})`} color="#00558d" />
                    <InfoRow
                      icon={Mars}
                      label={textTranslator(patient.gender, "PATIENTS") || "N/A"}
                      color={patient.gender === "Male" ? "#3B82F6" : "#EC4899"}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Action buttons */}
            <div
              className={defaultLanguage === "ar" ? 
                "absolute top-0 left-0 sm:top-2 sm:left-2 flex gap-2 w-full sm:w-auto":
                "absolute top-0 right-0 sm:top-2 sm:right-2 flex gap-2 w-full sm:w-auto"
              }
            >
              < LoadingButton 
                className="flex items-center gap-1.5 px-3.5 py-1.5 
              rounded-lg border !border-green-500 !bg-green-100 text-[13px] 
              font-medium !text-green-500 hover:!bg-green-200 transition-colors flex-1 sm:flex-none"
                onClick={handleDownload}
                disabled={downloading}
                loading={downloading}
                loadingText={textTranslator("Downloading...", "UI")}
              >
                <Download className="w-3.5 h-3.5" />{" "}
                <Text page="UI">Download</Text>
              </ LoadingButton >
              <button
                className="flex items-center gap-1.5 px-3.5 py-1.5 
                rounded-lg border border-blue-500 bg-blue-100 text-[13px] 
                font-medium text-blue-500 hover:bg-blue-50 transition-colors flex-1 sm:flex-none"
                onClick={showEditModalHandler}
              >
                <Edit className="w-3.5 h-3.5" /> <Text page="UI">Edit</Text>
              </button>
              <button
                className="px-2.5 py-1.5 rounded-lg border 
                border-red-500 bg-red-100 text-[13px] font-medium 
                text-red-500 hover:bg-red-200 transition-colors flex items-center gap-1.5 flex-1 sm:flex-none"
                onClick={handleDelete}
              >
                <Trash className="w-4 h-4 " /> <Text page="UI">Delete</Text>
              </button>
            </div>
          </div>
        )}
      </>
      <StatsGrid />
    </div>
  );
}

export default PersonalInfoCard;
